import React, { createContext, useContext, useEffect, useState } from 'react';
import { exchangeBetterAuthSession } from '@/lib/api';
import { sessionBootstrapStep } from '@/lib/session-bootstrap';
import { User } from '@/types/User';
import { clearAuthMarkers } from '@/lib/auth-prefs';
import { useAuthStore } from '@/stores/authStore';
import { onAuthEvent, signsOutThisTab } from '@/lib/auth-channel';
import { redirectToLoginForExpiredSession, willRedirectToLogin } from '@/lib/auth-redirect';
import { beginSessionExit } from '@/utils/session-exit';
import { clearPrefsOnSignOut } from '@/lib/ui-prefs';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: (loginPath?: string) => Promise<void>;
  setUser: (user: User | null) => void;
  /** The factory didn't answer the session check at load; this tab's stored user, if any, is kept. */
  sessionUnreachable: boolean;
  /** Ask for the session again (the "Can't reach the factory" Retry). */
  retrySession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    user,
    loading,
    error,
    login,
    logout,
    setUser,
    setLoading,
    updateUserAndToken,
    sessionUnreachable,
    setSessionUnreachable,
    setSessionChecked,
  } = useAuthStore();
  // Each Retry asks again; a run that StrictMode or a newer attempt replaced is dropped.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const tokenAtStart = useAuthStore.getState().user?.token ?? null;
      const sessionRes = await exchangeBetterAuthSession();
      if (cancelled) return;
      const step = sessionBootstrapStep(
        sessionRes,
        tokenAtStart,
        useAuthStore.getState().user?.token ?? null
      );
      if (step === 'apply') {
        updateUserAndToken(sessionRes.data!);
      } else if (step === 'signed-out') {
        setUser(null);
      } else if (step === 'unreachable') {
        // No answer, or a server error: not signed out. Keep this tab's stored user.
        // /auth/refresh needs the same cookie, so it can't succeed where /auth/session failed.
        setSessionUnreachable(true);
      }
      // 'superseded': the tab signed in, out or renewed while this was out; what it holds now stands.
      clearAuthMarkers();
      setLoading(false);
      // After the user (and token) above: API calls waiting for this go now (lib/session-ready.ts).
      setSessionChecked(true);
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [attempt, setUser, setLoading, setSessionUnreachable, setSessionChecked, updateUserAndToken]);

  const retrySession = () => {
    setSessionUnreachable(false);
    setLoading(true);
    setAttempt((n) => n + 1);
  };

  // Another tab signed out: so does this one. The sign-out itself already happened there.
  useEffect(
    () =>
      onAuthEvent((event) => {
        const state = useAuthStore.getState();
        if (event.type === 'signed-out') {
          if (!signsOutThisTab(event, { userId: state.user?.id })) {
            // Already signed out here (an expired session): nothing to leave, but this tab's
            // sessionStorage may still hold drafts.
            clearPrefsOnSignOut();
            return;
          }
          void (async () => {
            // Unmount the pages first, so their guards don't ask and their live connection
            // closes. Only when the page really leaves: it silences the guards until reload.
            if (willRedirectToLogin()) await beginSessionExit('Signing out…');
            // That tab cleared the shared localStorage; this clears this tab's sessionStorage, and
            // anything this tab wrote since.
            clearPrefsOnSignOut();
            redirectToLoginForExpiredSession();
          })();
        }
      }),
    []
  );

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    setUser,
    sessionUnreachable,
    retrySession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

if (typeof window !== 'undefined') {
  const checkAuthComplete = () => {
    const store = useAuthStore.getState();
    if (store.user && store.authAttempted) {
      clearAuthMarkers();
      store.setAuthAttempted(false);
    }
  };

  checkAuthComplete();
  const interval = setInterval(checkAuthComplete, 1000);
  setTimeout(() => clearInterval(interval), 10000);
}
